import { useTranslations } from 'next-intl'

import classnames from 'classnames'
import classNames from 'classnames'

import type { PortableTextComponentProps } from '~/components/atoms/BlockContent/BlockContent'
import BlockContent from '~/components/atoms/BlockContent/BlockContent'
import Fixture from '~/components/atoms/Fixture/Fixture'
import Text, { TextSizes } from '~/components/atoms/Text/Text'
import { ComponentTheme } from '~/components/themes'

import Container from '../Container/Container'
import type { CtaProps } from '../Ctas/Ctas'
import Ctas from '../Ctas/Ctas'

import { componentType, componentTypes } from './textBlockComponentTypes'

import styles from './TextBlock.module.css'

export interface TextBlockProps {
  _key: string
  body: PortableTextComponentProps['blocks']
  title?: string
  subTitle?: string
  smallTitle?: boolean
  smallBodyText?: boolean
  theme?: ComponentTheme
  verticalPadding?: boolean
  noPaddingBottom?: boolean
  testID?: string
  type?: componentType
  titleH1?: boolean
  moduleId?: string
  ctas?: CtaProps[]
  variant: null | 'magazine'
  borderBottom?: boolean
  marginTop?: boolean
  marginBottom?: boolean
}

export default function TextBlock({
  _key,
  body,
  title,
  subTitle,
  smallTitle = false,
  smallBodyText = false,
  theme = ComponentTheme.primary,
  verticalPadding,
  noPaddingBottom,
  testID,
  type = componentType.standard,
  titleH1,
  moduleId,
  ctas,
  borderBottom = false,
  marginTop = false,
  marginBottom = false,
  variant,
}: TextBlockProps) {
  const t = useTranslations('articlePage')

  const content = (
    <div
      className={classnames(
        styles.base,
        styles[type],
        variant && styles[variant],
        {
          [styles.borderBottom]: borderBottom,
          [styles.marginTop]: marginTop,
          [styles.marginBottom]: marginBottom,
        }
      )}
      data-testid={testID}
      id={_key}
    >
      {title && (
        <Text
          className={classNames(
            styles.title,
            smallTitle && styles.smallTitle,
            smallBodyText && styles.hasSmallBodyText
          )}
          size={
            smallTitle
              ? TextSizes.magSecondaryBodySml
              : componentTypes[type].title
          }
          Tag={titleH1 ? 'h1' : 'h2'}
          dataNoSnippet={true}
        >
          {type === componentType.question && <>{t('question')}</>}
          {title}
        </Text>
      )}
      {subTitle && (
        <Text
          className={styles.subTitle}
          size={TextSizes.secondaryTitleLrg}
          Tag="h3"
        >
          {subTitle}
        </Text>
      )}
      <div
        className={classnames(styles.bodyText, 'prose', {
          [styles.smallBodyText]: smallBodyText,
        })}
      >
        <BlockContent value={body} size={componentTypes[type].textSize} />
      </div>

      {ctas && (
        <div className={styles.ctas}>
          <Ctas
            theme={theme}
            ctas={ctas}
            center={
              !smallBodyText && type !== componentType.prominentTextBlock
                ? false
                : true
            }
            stack={!smallBodyText ? false : true}
            hideIcon={smallBodyText}
          />
        </div>
      )}
    </div>
  )

  return (
    <Fixture
      theme={theme}
      verticalPadding={verticalPadding}
      noPaddingBottom={noPaddingBottom}
      id={moduleId}
    >
      {variant === 'magazine' ? content : <Container>{content}</Container>}
    </Fixture>
  )
}
